import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { IOptionItem, ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defer, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { LoadingState } from '../../../../shared/types/loading.type';
import { outputError } from '../../../../shared/utils/output-error.function';
import { WorkerDictionaryClient } from '../../../../core/openapi/bff-admin-api/api/worker-dictionary.client';
import { MaintenanceDictionaryService } from '../../maintenance-dictionary.service';
import {
  CreateMaintenanceRequestsRequest,
  MaintenanceClient,
} from '../../../../core/openapi/bff-admin-api';

@Injectable()
export class MaintenanceRequestModalService {
  private readonly workerDictionaryClient = inject(WorkerDictionaryClient);
  private readonly maintenanceClient = inject(MaintenanceClient);
  private readonly dictionaryService = inject(MaintenanceDictionaryService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _workersLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.LOADING,
  );
  private readonly _createRequestLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.IDLE,
  );

  public readonly workersLoadingStatus: Signal<LoadingState> =
    this._workersLoadingStatus.asReadonly();
  public readonly createRequestLoadingStatus: Signal<LoadingState> =
    this._createRequestLoadingStatus.asReadonly();
  public readonly maintenanceTypeOptionsLoadingStatus: Signal<LoadingState> =
    this.dictionaryService.maintenanceTypesLoadingStatus;
  public readonly warrantyOptionsLoadingStatus: Signal<LoadingState> =
    this.dictionaryService.warrantyTypesLoadingStatus;
  public readonly errorOptionsLoadingStatus: Signal<LoadingState> =
    this.dictionaryService.errorsLoadingStatus;

  public readonly workerOptions$: Observable<IOptionItem<string>[]> = defer(() => {
    this._workersLoadingStatus.set(LoadingState.LOADING);
    return this.workerDictionaryClient.v1WorkerDictionarySearchPost({ skip: 0, take: 100 });
  }).pipe(
    map(({ items }) => (items ?? []).map((item) => ({ id: item.id, label: item.name }))),
    catchError((error) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.maintenance.modal.toast.workers-fetch-failed',
          { error: outputError(error, this.translateService.instant('errors.unknown')) },
        ),
      });
      return of([]);
    }),
    finalize(() => this._workersLoadingStatus.set(LoadingState.READY)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly warrantyOptions$: Observable<IOptionItem<number>[]> =
    this.dictionaryService.warrantyTypes$.pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          label: this.translateService.instant(`monitoring.maintenance.warrantyType.${item.code}`),
        })),
      ),
    );

  public readonly maintenanceTypeOptions$: Observable<IOptionItem<number>[]> =
    this.dictionaryService.maintenanceTypes$.pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          label: this.translateService.instant(
            `monitoring.maintenance.maintenanceType.${item.code}`,
          ),
        })),
      ),
    );

  public readonly errorOptions$: Observable<IOptionItem<number>[]> =
    this.dictionaryService.errors$.pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          label: this.translateService.instant(`monitoring.maintenance.error.${item.code}`),
        })),
      ),
    );

  public createMaintenanceRequest(request: CreateMaintenanceRequestsRequest): Observable<boolean> {
    this._createRequestLoadingStatus.set(LoadingState.LOADING);

    return this.maintenanceClient.v1MaintenancePost(request).pipe(
      map(() => true),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant(
            'monitoring.maintenance.modal.toast.request-created',
          ),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.maintenance.modal.toast.request-create-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });
        return of(false);
      }),
      finalize(() => this._createRequestLoadingStatus.set(LoadingState.READY)),
    );
  }
}
